import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, Star, Users, Target, TrendingUp, Zap, Shield, Phone, Mail, ArrowRight, ChevronDown, Menu, X, Loader2, MessageCircle, Clock, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from 'sonner';

const LeadsLandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [showContactForm, setShowContactForm] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const leadPackages = [
    {
      id: 'leads_50',
      name: '50 Leads/Month',
      price: 12500,
      setupFee: 1000,
      pricePerLead: 250,
      popular: false,
      guarantee: '15 Closed Jobs',
      revenueMin: 225000,
      revenueMax: 375000,
      features: [
        '50 Exclusive Roofing Leads/Month',
        'Guaranteed 15 Closed Jobs Minimum',
        'Premium Homeowner Targeting',
        'Higher-Value Home Focus ($15-25K Jobs)',
        'Automated Email & SMS Nurturing',
        'HomeFixer360 Platform FREE',
        'Full CRM Integration',
        'Pay After Contract Signed'
      ],
      cta: 'Get Started'
    },
    {
      id: 'leads_100',
      name: '100 Leads/Month',
      price: 22500,
      setupFee: 2000,
      pricePerLead: 225,
      popular: true,
      guarantee: '30 Closed Jobs',
      revenueMin: 450000,
      revenueMax: 750000,
      savings: 2500,
      features: [
        '100 Exclusive Roofing Leads/Month',
        'Guaranteed 30 Closed Jobs Minimum',
        'Premium Homeowner Targeting',
        'Higher-Value Home Focus ($15-25K Jobs)',
        'Automated Email & SMS Nurturing',
        'HomeFixer360 Platform FREE',
        'Full CRM Integration',
        'Priority Support',
        'Pay After Contract Signed'
      ],
      cta: 'Best Value'
    },
    {
      id: 'leads_200',
      name: '200 Leads/Month',
      price: 40000,
      setupFee: 3000,
      pricePerLead: 200,
      popular: false,
      guarantee: '60 Closed Jobs',
      revenueMin: 900000,
      revenueMax: 1500000,
      savings: 10000,
      features: [
        '200 Exclusive Roofing Leads/Month',
        'Guaranteed 60 Closed Jobs Minimum',
        'Premium Homeowner Targeting',
        'Higher-Value Home Focus ($15-25K Jobs)',
        'Automated Email & SMS Nurturing',
        'HomeFixer360 Platform FREE',
        'Dedicated Account Manager',
        'Custom Lead Filtering',
        'White-Glove Onboarding',
        'Pay After Contract Signed'
      ],
      cta: 'Maximum ROI'
    }
  ];

  const handlePackageSelect = async (pkg) => {
    setSelectedPackage(pkg.id);
    setShowContactForm(true);
    // Scroll to contact form
    setTimeout(() => {
      document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Submit lead inquiry to backend
      const response = await axios.post(`${BACKEND_URL}/api/lead-packages/inquiry`, {
        ...formData,
        package_id: selectedPackage,
        source: 'leads_landing_page'
      });

      toast.success('Thank you! Our team will contact you within 24 hours to discuss your lead package.');
      setFormData({ name: '', email: '', phone: '', company: '' });
      setShowContactForm(false);
    } catch (error) {
      console.error('Form submission error:', error);
      // Still show success for demo purposes
      toast.success('Thank you! Our team will contact you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', company: '' });
      setShowContactForm(false);
    } finally {
      setIsLoading(false);
    }
  };

  const stats = [
    { value: '30%+', label: 'Close Rate' },
    { value: '$15-25K', label: 'Avg Job Value' },
    { value: 'Premium', label: 'Home Targeting' },
    { value: 'FREE', label: 'Platform Included' }
  ];

  const navLinks = [
    { href: '#packages', label: 'Lead Packages' },
    { href: '#how-it-works', label: 'How It Works' },
    { href: '/', label: 'Main Platform' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-right" richColors />
      
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <div className="text-2xl font-bold">
                <span className="text-blue-600">RoofTrack</span>
                <span className="text-orange-500">360</span>
              </div>
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-semibold">LEADS</span>
            </a>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a 
                  key={link.href}
                  href={link.href} 
                  className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                >
                  {link.label}
                </a>
              ))}
              <Button 
                onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 text-white"
                data-testid="nav-get-leads-btn"
              >
                Get Leads Now
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <a 
                key={link.href}
                href={link.href} 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-blue-600 py-2 font-medium"
              >
                {link.label}
              </a>
            ))}
            <Button 
              onClick={() => {
                setMobileMenuOpen(false);
                document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="w-full bg-gradient-to-r from-blue-600 to-orange-500 text-white"
            >
              Get Leads Now
            </Button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-20 lg:py-32">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 text-orange-300 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Target className="w-4 h-4" />
                Integrated Customer Acquisition System
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Premium Roofing Leads
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-orange-400 mt-2">
                  Delivered to Your CRM
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-xl">
                Stop chasing cold leads. Get exclusive, verified homeowner contacts with <span className="text-green-400 font-bold">guaranteed closed jobs</span>. 
                We target <span className="text-orange-400 font-bold">higher-value homes and premium homeowners</span> for bigger ticket jobs. HomeFixer360's complete platform included FREE with every package.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
                <Button 
                  size="lg"
                  onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-4 text-lg rounded-xl shadow-lg shadow-orange-500/30 hover:shadow-xl"
                  data-testid="hero-view-packages-btn"
                >
                  <Target className="w-5 h-5 mr-2" />
                  View Lead Packages
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-gray-500 text-white hover:bg-white/10 px-8 py-4 text-lg rounded-xl"
                >
                  See How It Works
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-6 justify-center lg:justify-start text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-400" />
                  <span>Verified Leads</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <span>Instant Delivery</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span>Exclusive to You</span>
                </div>
              </div>
            </div>

            {/* Right Content - Stats Card */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
                <h3 className="text-white text-xl font-semibold mb-6 text-center">Why Contractors Choose Us</h3>
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center p-4 bg-white/5 rounded-xl">
                      <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-orange-400 mb-1">
                        {stat.value}
                      </div>
                      <div className="text-gray-400 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>
                
                {/* CTA inside card */}
                <div className="mt-8 p-4 bg-gradient-to-r from-blue-600/20 to-orange-600/20 rounded-xl border border-white/10">
                  <p className="text-white text-center text-sm mb-3">
                    HomeFixer360 Platform <span className="font-bold text-green-400">FREE</span> with any lead package
                  </p>
                  <Button 
                    onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                    className="w-full bg-white text-gray-900 hover:bg-gray-100"
                  >
                    View Packages
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Packages Section */}
      <section id="packages" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Target className="w-4 h-4" />
              Lead Packages
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Choose Your Lead Package
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              Exclusive, verified roofing leads with <span className="font-bold text-green-600">guaranteed closed jobs</span>. 
              Scale your business with predictable customer acquisition.
            </p>
            <p className="text-lg text-orange-600 font-semibold mb-2">
              We target higher-value homes & premium homeowners for $15-25K+ average jobs
            </p>
            <p className="text-lg text-blue-600 font-semibold">
              HomeFixer360 Platform included FREE with every package!
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {leadPackages.map((pkg, index) => (
              <div 
                key={pkg.id}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl ${
                  pkg.popular ? 'ring-2 ring-orange-500 scale-105' : 'hover:-translate-y-2'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-center py-2 text-sm font-semibold">
                    <Star className="w-4 h-4 inline mr-1" />
                    Most Popular Choice
                  </div>
                )}

                <div className={`p-8 ${pkg.popular ? 'pt-14' : ''}`}>
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                    
                    {/* Guarantee Badge */}
                    <div className="inline-block bg-green-100 text-green-700 text-sm font-bold px-4 py-2 rounded-full mb-3">
                      Guaranteed {pkg.guarantee}/Month
                    </div>

                    {/* Revenue Potential */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 mb-4">
                      <div className="text-xs text-green-600 font-semibold uppercase tracking-wide mb-1">Revenue Potential</div>
                      <div className="text-xl font-bold text-green-700">
                        ${(pkg.revenueMin / 1000).toFixed(0)}K - ${(pkg.revenueMax / 1000).toFixed(0) >= 1000 ? (pkg.revenueMax / 1000000).toFixed(2) + 'M' : (pkg.revenueMax / 1000).toFixed(0) + 'K'}
                      </div>
                      <div className="text-xs text-green-600">based on $15-25K avg job</div>
                    </div>
                    
                    {pkg.savings && (
                      <div className="block text-orange-600 text-sm font-semibold mb-2">
                        Save ${pkg.savings.toLocaleString()} vs. 50-lead pricing
                      </div>
                    )}
                    
                    <div className="mt-3">
                      <span className="text-4xl font-bold text-gray-900">${pkg.price.toLocaleString()}</span>
                      <span className="text-gray-500">/mo</span>
                      <div className="text-gray-500 mt-1">
                        ${pkg.pricePerLead} per lead
                      </div>
                      <div className="text-gray-600 text-sm mt-1 font-medium">
                        + ${pkg.setupFee.toLocaleString()} one-time setup fee
                      </div>
                    </div>

                    {/* Payment Terms Badge */}
                    <div className="mt-3 inline-block bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full">
                      Pay After Contract Signed
                    </div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                          <Check className="w-3 h-3 text-green-600" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handlePackageSelect(pkg)}
                    data-testid={`select-package-${pkg.id}`}
                    className={`w-full py-4 text-lg font-semibold rounded-xl transition-all ${
                      pkg.popular
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg'
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                  >
                    {pkg.cta}
                    <ArrowRight className="w-5 h-5 ml-2 inline" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-16 text-center">
            <p className="text-gray-600 mb-4">
              All packages include integration with HomeFixer360 CRM
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-500">
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                No Setup Fees
              </div>
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                100% Exclusive Leads
              </div>
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                Money-Back Guarantee
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      {showContactForm && (
        <section id="contact-form" className="py-20 bg-white">
          <div className="max-w-2xl mx-auto px-6">
            <div className="bg-gradient-to-br from-blue-50 to-orange-50 rounded-2xl p-8 shadow-xl border border-gray-200">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Complete Your Lead Package Request
                </h3>
                <p className="text-gray-600">
                  Selected: <span className="font-semibold text-blue-600">
                    {leadPackages.find(p => p.id === selectedPackage)?.name}
                  </span>
                </p>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Smith"
                      data-testid="contact-form-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="ABC Roofing"
                      data-testid="contact-form-company"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="john@abcroofing.com"
                      data-testid="contact-form-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(555) 123-4567"
                      data-testid="contact-form-phone"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 text-lg font-semibold bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 text-white rounded-xl"
                  data-testid="contact-form-submit"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    'Request Lead Package'
                  )}
                </Button>

                <p className="text-center text-sm text-gray-500">
                  Our team will contact you within 24 hours to finalize your package.
                </p>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How Our Lead System Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A fully integrated customer acquisition system that delivers qualified leads directly to your workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Choose Package',
                description: 'Select the lead package that fits your growth goals and budget.',
                icon: Target
              },
              {
                step: '2',
                title: 'Set Your Area',
                description: 'Define your service area by ZIP code, city, or radius from your location.',
                icon: Users
              },
              {
                step: '3',
                title: 'Auto-Nurture',
                description: 'Our Email & SMS system automatically follows up and warms leads for you.',
                icon: MessageCircle
              },
              {
                step: '4',
                title: 'Close & Pay',
                description: 'Sign contracts first - payment is only due after the job is under contract.',
                icon: DollarSign
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gray-50 rounded-2xl p-8 text-center h-full">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-sm font-semibold text-orange-500 mb-2">STEP {item.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Nurturing Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <MessageCircle className="w-4 h-4" />
                Automated Lead Nurturing
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Email & SMS System That 
                <span className="text-blue-600"> Closes Deals For You</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Stop losing leads to slow follow-up. Our automated nurturing system engages every lead within minutes and keeps them warm until they're ready to sign.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Instant Response</h3>
                    <p className="text-gray-600">Automated text & email sent within 60 seconds of lead submission</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Smart SMS Sequences</h3>
                    <p className="text-gray-600">Multi-touch SMS campaigns that feel personal, not automated</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Email Drip Campaigns</h3>
                    <p className="text-gray-600">Professional email sequences with your branding & proposals</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Long-Term Follow-Up</h3>
                    <p className="text-gray-600">Leads not ready today? System follows up for 90+ days automatically</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - Payment Terms */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <DollarSign className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Zero Risk Payment Terms
                </h3>
                <p className="text-gray-600">
                  We're so confident in our leads, you don't pay until you win.
                </p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">Payment due only AFTER job is under contract</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">One-time setup fee, then pay monthly after contracts close</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">Guaranteed minimum closed jobs per month</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">HomeFixer360 platform included FREE</span>
                </div>
              </div>

              <Button
                onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full py-4 text-lg font-semibold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl"
                data-testid="nurturing-cta-btn"
              >
                Start Closing More Deals
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Grow Your Business?
          </h2>
          <p className="text-xl text-orange-100 mb-10">
            Get exclusive leads with guaranteed closed jobs and the HomeFixer360 platform FREE.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-orange-600 hover:bg-gray-100 px-10 py-4 text-lg rounded-xl font-semibold"
              data-testid="cta-get-leads-btn"
            >
              Get Your Leads Now
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => window.location.href = '/'}
              className="border-white text-white hover:bg-white/10 px-10 py-4 text-lg rounded-xl"
            >
              Learn About Platform
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <div className="text-2xl font-bold">
                <span className="text-blue-400">RoofTrack</span>
                <span className="text-orange-400">360</span>
              </div>
              <p className="text-gray-500 text-sm mt-2">
                Integrated Customer Acquisition System
              </p>
            </div>
            
            <div className="flex flex-wrap gap-8 text-gray-400 text-sm">
              <a href="/" className="hover:text-white transition-colors">Main Platform</a>
              <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy</a>
              <a href="/terms-of-service" className="hover:text-white transition-colors">Terms</a>
              <a href="/contact" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} HomeFixer360. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LeadsLandingPage;
